import React from "react";
import { ActionPointDeadlineExceededNotificationStateData, UserNotification } from "../../amorg-common";
import { getUserNotifications, updateUserNotification } from "../../api/notificationClientApi";
import { getMaybeActiveUserOrRefresh } from "../../api/serverApiHelpers";
import { Button } from "../../designSystem/button/Button";
import { LinkButton } from "../../designSystem/button/LinkButton";
import { Card } from "../../designSystem/Card";
import { colors } from "../../designSystem/colors/color";
import { Flex, FlexItem } from "../../designSystem/compositeComponents/FlexHelpers";
import { CheckboxField } from "../../designSystem/formField/CheckboxField";
import { Icon } from "../../designSystem/icons/Icons";
import { SingleColumnLayout } from "../_layouts/SingleColumnLayout";
import { LoadingPageOverlay } from "../../designSystem/LoadingPageOverlay";
import { PageHeader } from "../../designSystem/PageHeader";
import { P } from "../../designSystem/typography/Body";
import { H4 } from "../../designSystem/typography/Heading";
import { clientAccessHelper } from '../../utils/clientAccessHelper';
import { toTextDateString } from "../../utils/date";
import { ProtectedPageContext } from '../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../_pageWrappers/MemberPageWrapper';

export function NotificationsPage():JSX.Element
{
    return (
        <MemberPageWrapper selectedMenuItemKey='notifications' breadCrumbs={[{ text: 'Notifikationer', url: '' }]}>
            <PageHeader 
                narrow
                headerIcon='assignment_late'
                headerText="Notifikationer" />
            <SingleColumnLayout narrow>
                <NotificationsPageContent />
            </SingleColumnLayout>
        </MemberPageWrapper>
    );
}

function NotificationsPageContent():JSX.Element
{
    const [loading, setLoading] = React.useState(true);
    const [userId, setUserId] = React.useState<string | undefined>(undefined);
    const [showAll, setShowAll] = React.useState(false);

    const [notifications, setNotifications] = React.useState<UserNotification[]>([]);

    const [notificationsIgnoredCount, setNotificationsIgnoredCount] = React.useState(0);

    React.useEffect(()=>{
        const getData = async ()=>{
            const user = await getMaybeActiveUserOrRefresh([]);
            setUserId(user.tokenUser.userId);
            const notifs = await getUserNotifications(user.tokenUser.userId, false, showAll);
            setNotifications(notifs);
            
            const notifsIncludingIgnored = await getUserNotifications(user.tokenUser.userId, false, true);
            const ignoredCount = notifsIncludingIgnored.filter(obj => obj.ignored === true);
            setNotificationsIgnoredCount(ignoredCount.length)
            setLoading(false);
        };
        getData();
    }, []);

    async function onChange()
    {
        setLoading(true);
        const notifs = await getUserNotifications(userId, false, showAll);
        setNotifications(notifs);

        const notifsIncludingIgnored = await getUserNotifications(userId, false, true);
        const ignoredCount  = notifsIncludingIgnored.filter(obj => obj.ignored === true);
        setNotificationsIgnoredCount(ignoredCount.length)
        setLoading(false);
    }

    async function handleClickShowAll(_:string, checked:boolean)
    {
        setLoading(true);
        const includeIgnored = checked;
        const notifs = await getUserNotifications(userId, false, includeIgnored);
        setNotifications(notifs);
        setShowAll(!showAll);
        setLoading(false);
    }


    const protectedUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(protectedUser, ['ViewNotifications']);

    return (
        <>
                <LoadingPageOverlay loading={loading} />

                {!hasAccess
                    ? <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>
                    :
                    <Flex flexDirection="column" gap={16}>
                        <Flex flexDirection="row" justifyContent="space-between">
                            <H4>{notifications.length || 'ingen'} notifikationer</H4>
                            {notificationsIgnoredCount >= 1 ? 
                                <CheckboxField 
                                    text='Vis alle'
                                    value='Vis alle'
                                    checked={showAll}
                                    onChange={handleClickShowAll} 
                                    label={"Vis skjulte"}
                                /> : <div style={{height:'40px', display:'flex', alignItems:'center'}}>Ingen skjulte</div>
                            }
                            
                        </Flex>
                        {notifications.map(n => 
                            <NotificationView key={n.notificationId} notification={n} activeUserId={userId || ''} onChange={onChange} />
                        )}
                    </Flex>
                }
            </>
    );
}

type NotificationViewProps = {
    activeUserId:string,
    notification:UserNotification,
    onChange:()=>void
}
function NotificationView(props:NotificationViewProps):JSX.Element
{

    async function hideNotification(notificationId:string, hide:boolean){
        await updateUserNotification(notificationId, props.activeUserId, hide);
        props.onChange();
    }

    const n = props.notification;
    const notificationIgnoredIcon = n.ignored ? "interface_actions_view" : "interface_actions_hidden";
    const notificationIgnoredText = n.ignored ? "Vis notifikation" : "Skjul notifikation";

    return (
        <>
        <Card variant="outlined" disabled={n.ignored} padding="16px">

            <Flex gap={8}>
                <Icon size="large" name='interface_actions_notificationOn' color={colors.icon.warning}  />
                <FlexItem flexGrow="1">
                    {getNotificationContent(n)}
                </FlexItem>
               
               <FlexItem flexGrow="0">
                    <Button 
                        variant="text" 
                        toolTipText={notificationIgnoredText}
                        icon={notificationIgnoredIcon}
                        onClick={() => hideNotification(props.notification.notificationId, !props.notification.ignored)} />
               </FlexItem>
            </Flex>
        </Card>
        </>
    );
}

function getNotificationContent(notif:UserNotification):React.ReactElement
{
    if(notif.type ==='actionPointDeadlineExceeded')
    {
        const state = notif.data as ActionPointDeadlineExceededNotificationStateData;
        if(state.actionPointType === 'ActionPlanTopic')
        {
            return ( 
                <Flex flexDirection="column" gap={16}>
                    <Flex flexDirection="column" gap={8}>
                        <H4>Handlepunkt med overskredet deadline d. ({toTextDateString(new Date(state.actionPointDeadline))})</H4>
                        <P><i>{`"${state.actionPointDescription}"`}</i></P>
                    </Flex>
                    <FlexItem flexGrow="0">
                        <LinkButton variant="outlined" href={`workplaceassessments/${state.activityId}?tab=actionplans&actionPlanId=${state.actionPlanId}`} text="Gå til handleplan" />
                    </FlexItem>
                    
                </Flex>
            );
        }
        else
        {
            return ( 
                <Flex flexDirection="column" gap={16}>
                    <Flex flexDirection="column" gap={8}>
                        <H4>Handlepunkt med overskredet deadline d. ({toTextDateString(new Date(state.actionPointDeadline))})</H4>
                        <P>Handlepunktet <i>{state.actionPointDescription}</i></P>
                    </Flex>
                    <FlexItem flexGrow="0">
                        <LinkButton variant="outlined" href={`workgroupmeetings/${state.activityId}`} text="Gå til mødet" />
                    </FlexItem>
                </Flex>
            );
        }
    }

    else if(notif.type == 'noMeetingPlanned')
    {
        return ( 
            
            <Flex flexDirection="column" gap={8}>
                <H4>Intet møde planlagt</H4>
                <P>Det er mere end en uge siden sidste AMO møde er afholdt, og ingen nye møder er planlagt</P>
            </Flex>
            
        );
    }

    return ( 
        <Flex flexDirection="column" gap={8}>
            <H4>[{notif.type}]</H4>
            <P>[active:{notif.active ? 'active' : 'inactive'}, ignored:{notif.ignored ? 'ignored' : 'shown'}]</P>
        </Flex>
    );
}